/**
 * Screen-share client (iter135)
 * ─────────────────────────────
 *
 * Thin JS shim around the auth-gated backend endpoints in
 * ``/app/backend/screen_share_routes.py``. Responsibilities:
 *
 *   1. Lifecycle calls — start / end / join / leave — using the
 *      shared ``api`` axios client so X-User-Email + X-User-ID
 *      headers attach automatically.
 *   2. Presenter preview — captures the user's screen via
 *      ``navigator.mediaDevices.getDisplayMedia()`` and exposes
 *      the resulting ``MediaStream`` so the UI can render a
 *      local thumbnail.
 *   3. WebSocket attach — opens ``/api/conv/share/ws?token=…`` so
 *      the auth gateway flips Pending → Active and emits the
 *      ``share_started`` System Message.
 *
 * What this module DOES NOT do (yet):
 *
 *   - Actual SDP/ICE relay between Peers. Frame transport requires
 *     either the vendored TypeScript sub-module compiled (P2P) or
 *     LiveKit credentials provisioned (SFU). With ``SFU_PROVIDER=
 *     none`` the gateway's ``/share/sfu/token`` returns 501, and
 *     this module surfaces the share as "preview-only".
 *
 *   - Multi-Peer fan-out. P2P direct mesh and SFU forwarding are
 *     both stubbed at the relay layer; the auth contract works
 *     end-to-end and the system messages emit correctly.
 *
 * The auth layer is fully functional regardless. Tests and audit
 * exercise every state transition.
 */
import api from "@/lib/api";

const WS_BASE = (() => {
  const u = new URL(process.env.REACT_APP_BACKEND_URL || window.location.origin);
  u.protocol = u.protocol === "https:" ? "wss:" : "ws:";
  return `${u.origin}/api/conv/share/ws`;
})();

// ── Lifecycle helpers ──────────────────────────────────────────

/** Start a Share. Returns ``{ share_id, token, peer_id, expires_at }``. */
export async function startShare(conversationId) {
  const r = await api.post(`/conv/conversations/${conversationId}/share`);
  return r.data;
}

/** End a Share (Presenter only). Returns ``{ state, reason, ended_at }``. */
export async function endShare(conversationId, shareId) {
  const r = await api.post(`/conv/conversations/${conversationId}/share/${shareId}/end`);
  return r.data;
}

/** Viewer joins the live Share. Returns ``{ share_id, token, peer_id, … }``. */
export async function joinShare(conversationId) {
  const r = await api.post(`/conv/conversations/${conversationId}/share/join`);
  return r.data;
}

/** Viewer leaves voluntarily. Returns ``{ left, reason }``. */
export async function leaveShare(conversationId, shareId, reason = "viewer_terminated") {
  const r = await api.post(
    `/conv/conversations/${conversationId}/share/${shareId}/leave`,
    { reason },
  );
  return r.data;
}

/** Read live Share status. Returns ``{ share }`` (null if no live share).
 *
 *  iter136.16 — silent: true, this poll fires every few seconds while
 *  a share is live and is gracefully retried; transient network blips
 *  during heavy WebRTC traffic shouldn't surface a global toast. */
export async function getShareStatus(conversationId) {
  const r = await api.get(`/conv/conversations/${conversationId}/share/status`, { silent: true });
  return r.data?.share || null;
}

// ── Presenter capture ──────────────────────────────────────────

/** Capture the user's screen. Returns the MediaStream or throws.
 *  iter135.18 — requests system/tab audio as well so computer sound
 *  reaches viewers. Chrome on Win/Mac captures full-screen audio;
 *  Chrome on Linux captures tab-audio only; Firefox and Safari
 *  silently return video-only (detectable via ``getAudioTracks()``
 *  being empty — ``PresenterContext`` surfaces a small indicator). */
export async function captureScreen() {
  if (!navigator.mediaDevices?.getDisplayMedia) {
    throw new Error("Screen sharing is not supported in this browser");
  }
  return navigator.mediaDevices.getDisplayMedia({
    video: { frameRate: { ideal: 15, max: 30 } },
    // Faithful computer audio — do NOT apply voice-processing to a
    // screen-share stream (users share music / game / video call
    // audio, not voice).
    audio: {
      echoCancellation: false,
      noiseSuppression: false,
      autoGainControl: false,
    },
  });
}

// ── WS attach ──────────────────────────────────────────────────

/** Open the gateway-wrapped signaling WS. Returns the WebSocket
 *  ready to send/receive opaque envelopes. The first message the
 *  gateway pushes is ``{type: "joined", …}`` confirming the bind.
 *
 *  iter135.9 — sends an application-level ``ping`` every 25 s so
 *  the K8s ingress / nginx proxy doesn't close an "idle" WS after
 *  its 30-60 s no-traffic timeout. The gateway acks pings via the
 *  generic relay handshake (``{type: "ack", ref: "ping"}``); we
 *  ignore the ack on the client side. Without this, idle timeouts
 *  triggered the iter135.8 abnormal-end handler mid-presentation
 *  even when LiveKit was relaying frames perfectly.
 */
export function openSignalingWs(token, { onJoined, onClose, onError } = {}) {
  const ws = new WebSocket(`${WS_BASE}?token=${encodeURIComponent(token)}`);
  let pingTimer = null;
  ws.addEventListener("open", () => {
    pingTimer = setInterval(() => {
      try {
        if (ws.readyState === WebSocket.OPEN) {
          ws.send(JSON.stringify({ type: "ping", t: Date.now() }));
        }
      } catch { /* noop */ }
    }, 25000);
  });
  ws.addEventListener("message", (ev) => {
    try {
      const msg = JSON.parse(ev.data);
      if (msg.type === "joined" && onJoined) onJoined(msg);
    } catch {
      /* drop malformed payloads */
    }
  });
  ws.addEventListener("close", (ev) => {
    if (pingTimer) { clearInterval(pingTimer); pingTimer = null; }
    onClose?.(ev);
  });
  if (onError) ws.addEventListener("error", onError);
  return ws;
}
